import { useForumType } from '@/components/hooks/useForumType';
import { useTracking } from '@/lib/analyticsEvents';
import React from 'react';
import { useCurrentUser } from '../common/withUser';
import { useNotifyMe } from '../hooks/useNotifyMe';
import { useOptimisticToggle } from '../hooks/useOptimisticToggle';
import classNames from 'classnames';
import { userGetDisplayName } from '@/lib/collections/users/helpers';
import { useMutationNoCache } from '@/lib/crud/useMutationNoCache';
import { gql } from '@/lib/generated/gql-codegen';
import LWTooltip from "../common/LWTooltip";
import { useDialog } from '../common/withDialog';
import LoginPopup from "../users/LoginPopup";
import { defineStyles } from '@/components/hooks/defineStyles';
import { useStyles } from '@/components/hooks/useStyles';

const styles = defineStyles('FollowUserButton', (theme: ThemeType) => ({
  root: {
    ...theme.typography.commentStyle,
    color: theme.palette.primary.main,
    cursor: "pointer",
  },
  subscribed: {
    color: "unset",
  },
  ultraFeedRoot: {
    ...theme.typography.commentStyle,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 32,
    width: 90,
    padding: '0 8px',
    borderRadius: 4,
    fontSize: 16,
    fontWeight: 500,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.text.alwaysWhite,
    border: 'none',
    '&:hover': {
      opacity: 0.9,
    },
  },
  ultraFeedSubscribed: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[500],
    '&:hover': {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.grey[900],
    },
  },
}));

export const FollowUserButton = ({user, styleVariant = "default"}: {
  user: UsersMinimumInfo,
  styleVariant?: "default" | "ultraFeed",
}) => {
  const { forumType } = useForumType();
  const classes = useStyles(styles);
  const { captureEvent } = useTracking();
  const currentUser = useCurrentUser();
  const { openDialog } = useDialog();

  const { isSubscribed, onSubscribe, disabled } = useNotifyMe({
    document: user,
    overrideSubscriptionType: "newActivityForFeed",
    hideFlashes: true,
  });

  const onSubscribeWithDM = async (ev: React.MouseEvent<Element, MouseEvent>) => {
    const onSubscribeNullSafe = onSubscribe ?? (() => Promise.resolve())

    await onSubscribeNullSafe(ev);
    return await sendNewFollowDM({ variables: { eventType: "newFollowSubscription" } });
  }

  const [subscribed, toggleSubscribed] = useOptimisticToggle(
    isSubscribed ?? false,
    onSubscribeWithDM
  );

  const [sendNewFollowDM] = useMutationNoCache(gql(`
    mutation sendEventTriggeredDM($eventType: String!) {
      sendEventTriggeredDM(eventType: $eventType)
    }
  `));

  const handleSubscribe = (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!currentUser) {
        openDialog({
          name: "LoginPopup",
          contents: ({ onClose }) => <LoginPopup onClose={onClose} />,
        });
      return;
    }
    void toggleSubscribed(ev);
    captureEvent("followUserButtonClick", {subcribedToUser: user._id, subscribed: !subscribed})
  }

  const followTooltip = `${userGetDisplayName(user, forumType)}'s content will appear in your feed`

  if (disabled) {
    return null;
  }

  const rootStyle = styleVariant === "ultraFeed" ? classes.ultraFeedRoot : classes.root;
  const subscribedStyle = styleVariant === "ultraFeed" ? classes.ultraFeedSubscribed : classes.subscribed;

  return <div 
    className={classNames(rootStyle, {[subscribedStyle]: subscribed})} 
    onClick={handleSubscribe}
  >
    <LWTooltip title={followTooltip} placement="top" disabled={subscribed}>
      {subscribed ? "Following" : "Follow"}
    </LWTooltip>
  </div>;
}

export default FollowUserButton;


